import { BuyerNewTenderService } from './../../buyer/services/buyer-new-tender.service';
import { HttpClient, HttpEventType } from "@angular/common/http";
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { NOTIFICATION_KEYWORDS, PAGINATION, ROUTES, SESSION_STORAGE, TENDER_MODAL_STYLE, TENDER_MODES, TENDER_STATUS } from "src/app/constants";
import { PaginatorState } from "src/app/services/crud-table/models/paginator.model";
import { SortState } from "src/app/services/crud-table/models/sort.model";
import { ITableState } from "src/app/services/crud-table/models/table.model";
import { UtilityService } from "src/app/services/utility.service";
import { LastUsedService } from "../../../services/LastUsedService";
import { BuyerTenderModel } from "../../buyer/models/buyer-tender.model";
import { BuyerTenderService } from "../../buyer/services/buyer-tender.service";

@Component({
	selector: "app-add-tender-product",
	templateUrl: "./add-tender-product.component.html",
	styleUrls: ["./add-tender-product.component.scss"]
})

export class AddTenderProductComponent implements OnInit, OnChanges {

	@Input() displayModal;
	@Input() selectedProduct;
	@Output() modalStateChange = new EventEmitter<boolean>();

	draftTenders: Array<object> = [];
	tenderModalStyle: object = TENDER_MODAL_STYLE;
	editId: string;
	createId: string;
	nullVolumeProducts: Array<object> = [];
	totalDraftTenders: number = 0;
	currentLanguage;
	categoryNotMatched: boolean = false;
	selectedDraftTenderName: string = '';

	draftTendersState: ITableState = {
		paginator: new PaginatorState(),
		sorting: new SortState("createdAt", "desc"),
		filter: { status: TENDER_STATUS.DRAFT },
		searchTerm: ""
	};

	tenderTableColumns = [
		{
			title: "Name",
			width: "60%"
		},
		{
			title: "Volume",
			width: "20%"
		},
		{
			title: "Action",
			align: "center",
			width: "20%"
		}
	];

	constructor(
		@Inject(HttpClient) http,
		private utilityService: UtilityService,
		private translateService: TranslateService,
		private spinnerService: NgxSpinnerService,
		private lastUsedService: LastUsedService,
		private buyerTenderService: BuyerTenderService<BuyerTenderModel>,
		private buyerNewTenderService: BuyerNewTenderService<BuyerTenderModel>

	) {

		this.currentLanguage = this.translateService.currentLang;
		this.translateService.onLangChange.subscribe(language => {
			this.currentLanguage = language.lang;
		});
	}

	ngOnInit(): void {
		this.buyerTenderService.isLoading$.subscribe((isLoading) => {
			isLoading === true
				? this.spinnerService.show()
				: this.spinnerService.hide();
		});
	}

	ngOnChanges(changes): void {
		if (changes && changes.displayModal) {
			this.displayModal = changes.displayModal.currentValue;
		}
		if (changes && changes.selectedProduct) {
			this.checkIfProductExistsInDraft();
		}
	}

	closeModal(data): void {
		this.categoryNotMatched = false;
		this.modalStateChange.emit(data);
		this.draftTendersState.paginator.page = 1;
	}

	/**
	 * Handle edit data set functionality
	 * @param id : id of data set to edit
	 */
	startEdit(data: object, field: string, type: string): void {
		if (type === "edit") {
			this.editId = data["id"];
		} else if (type === "create") {
			this.createId = data["id"];
		}

		const index = this.draftTenders.findIndex(dataObject => dataObject["id"] === data["id"]);
		this.draftTenders[index][field] = data[field];
		this.checkForNullEntries(field);
	}

	/**
	 * Handle stop edit data set functionality
	 */
	stopEdit(type: string, data?: object): void {
		if (type === "edit") {
			this.editId = null;
			this.selectedProduct['volume'] = data['volume'];
			this.checkForNullEntries("volume");
		} else if (type === "create") {
			this.createId = null;
			this.checkForNullEntries("name");
		}

	}

	/**
	 * Check for null product price data
	 */
	checkForNullEntries(field: string): void {
		this.nullVolumeProducts = this.draftTenders.filter(data => !data[field]);
	}

	checkForVolume() {
		return !this.selectedProduct?.volume || this.selectedProduct?.volume < (this.selectedProduct?.minimumOrderVolume);
	}

	/**
	 * Handle adding tenders flow
	 * @param id : id of the added product tender
	 */
	addToTender(tenderData) {
		const tenderProduct = { ...this.selectedProduct };

		delete tenderProduct.contract;
		if (tenderData['products'].length) {
			// set new product category manually according to tender category
			tenderProduct.categoryId = tenderData['products'][0].categoryId;
		}

		// set volume if not set
		if (!tenderProduct.volume) {
			tenderProduct.volume = 1;
		}

		this.lastUsedService.addProductToLastUsed(tenderProduct.id);

		// tender has products and other details
		if (tenderData['products'].length && tenderData['products'][0].categoryId === tenderProduct.categoryId) {
			this.buyerNewTenderService.setTenderStatus({
				status: tenderData.status,
				tenderId: tenderData._id ? tenderData._id : ''
			});

			this.buyerNewTenderService.setSelectedProducts([...tenderData['products'], tenderProduct]);
			this.buyerNewTenderService.setSelectedCategory(tenderData['products'][0].category);
			this.buyerNewTenderService.setAttachments(tenderData['attachments'] ? tenderData['attachments'] : []);

			let deliveryAddresses = new Set();
			if (tenderData && tenderData['deliveryAddress'].length) {
				tenderData['deliveryAddress'].forEach(address => {
					deliveryAddresses.add(address.id);
				});
			}
			this.buyerNewTenderService.setDeliveryAddresses({
				addressInfo: tenderData['deliveryAddress'] ? tenderData['deliveryAddress'] : [],
				deliveryAddress: deliveryAddresses
			});

			this.buyerNewTenderService.setPaymentInfo({
				notes: tenderData['notes'] ? tenderData['notes'] : '',
				paymentInfo1: tenderData['paymentInfo1'] ? tenderData['paymentInfo1'] : '',
				paymentInfo2: tenderData['paymentInfo2'] ? tenderData['paymentInfo2'] : ''
			});

			this.buyerNewTenderService.setDeadlineData({
				deadline: tenderData['deadLineDate'] ? tenderData['deadLineDate'] : null,
				title: tenderData['name'] ? tenderData['name'] : '',
				description: tenderData['description'] ? tenderData['description'] : '',
				activateTenderOn:tenderData['activateTenderOn'] ? tenderData['activateTenderOn'] : null
			});

			this.buyerNewTenderService.setPrivateTenderInfo({
				tenderMode: tenderData['isPrivate'] ? TENDER_MODES.PRIVATE : TENDER_MODES.PUBLIC,
				invitedSuppliers: tenderData['invitedOrganizationDetails'] ? tenderData['invitedOrganizationDetails'] : [],
				invitedEmails: tenderData['invitedEmails'] ? tenderData['invitedEmails'] : []
			});

			if (tenderData.status === 'draft') {
				this.buyerNewTenderService.updateDraftTender(tenderData._id).subscribe((response) => {
					if (response.type === HttpEventType["Response"]) {
						this.utilityService.navigate(ROUTES.BUYER_ROUTE + ROUTES.NEW_TENDER_ROUTE);
						this.closeModal("tender");
					}
				});
			} else {
				this.buyerNewTenderService.updateTender(tenderData._id).subscribe((response) => {
					if (response.type === HttpEventType["Response"]) {
						this.utilityService.navigate(ROUTES.BUYER_ROUTE + ROUTES.NEW_TENDER_ROUTE);
						this.closeModal("tender");
					}
				});
			}
		} else if (!tenderData['products'].length) { // tender is new. first product is being added
			this.buyerNewTenderService.setTenderStatus({
				status: tenderData.status,
				tenderId: tenderData._id ? tenderData._id : ''
			});

			if (tenderData._id) {
				// set tender id in local storage
				this.utilityService.setToLocalStorage('tender', {
					id: tenderData._id,
					shortId: tenderData.tenderId
				});
			}

			this.buyerNewTenderService.setSelectedProducts([tenderProduct]);
			// this.buyerNewTenderService.products$ = [this.selectedProduct];

			this.buyerNewTenderService.updateDraftTender(tenderData._id).subscribe((response) => {
				if (response.type === HttpEventType["Response"]) {
					this.utilityService.navigate(ROUTES.BUYER_ROUTE + ROUTES.NEW_TENDER_ROUTE);
					this.closeModal("tender");
				}
			});
		} else {
			this.selectedDraftTenderName = tenderData.name;
			this.categoryNotMatched = true;
		}

		// this.utilityService.setToSessionStorage( SESSION_STORAGE.TENDER, { products: this.draftTenders[index]["products"] } );
		// this.utilityService.setToSessionStorage( SESSION_STORAGE.TENDER_STATUS, this.draftTenders[index] );
	}

	/**
	 * Check if product is already existed in draft orders
	 */
	checkIfProductExistsInDraft(): void {
		this.draftTenders.map(tender => {
			if (tender["products"].findIndex(product => product['productNumber'] === this.selectedProduct["productNumber"]) !== -1) {
				tender["added"] = true;
			} else {
				delete tender["added"];
			}
		});
	}

	onQueryParamsChange(data): void {
		this.draftTendersState.paginator.page = data.pageIndex;
		// this.spinnerService.show();
		this.buyerTenderService.findDraftTenders(this.draftTendersState);
		this.buyerTenderService.draftTenders$.subscribe(response => {
			if (response) {
				this.draftTenders = response;
				this.checkIfProductExistsInDraft();
			}
		});
		this.buyerTenderService.totalItems$.subscribe(response => {
			this.totalDraftTenders = response;
		});
		// .subscribe( response => {
		// 	if ( response ) {
		// 		this.draftTenders = response.items;
		// 		this.totalDraftTenders = response.total;
		// 		this.checkIfProductExistsInDraft();
		// 	}
		// 	this.spinnerService.hide();
		// }, ( error ) => {
		// 	this.spinnerService.hide();
		// 	console.error( error );
		// } );
	}

	/**
	 * Create a new tender with selected product.
	 */
	addNewTender(): void {
		// this.utilityService.deleteFromSessionStorage( SESSION_STORAGE.TENDER );
		// this.utilityService.deleteFromSessionStorage( SESSION_STORAGE.TENDER_STATUS );
		// this.utilityService.setToSessionStorage( SESSION_STORAGE.TENDER, { products: [ this.selectedProduct ] } );

		this.buyerNewTenderService.setTenderStatus({
			status: 'addToTender',
		});

		this.buyerNewTenderService.setSelectedProducts([this.selectedProduct]);
		this.lastUsedService.addProductToLastUsed(this.selectedProduct.id);

		this.utilityService.navigate(ROUTES.BUYER_ROUTE + ROUTES.NEW_TENDER_ROUTE);
		this.closeModal("tender");
	}
}
